/**
 * Every number on `/overview`, computed here and nowhere else.
 *
 * This file is pure — dates in, counts out, no React, no clock, no `Date.now()` —
 * for the reason `web/src/attendance/month.ts` exists: the interesting cases on a
 * dashboard are all edges (a truncated page, a window with no rows at all, a
 * denominator of zero, a day the rollup has not reached), and an edge case reachable
 * only by mounting a component is an edge case nobody writes a test for.
 *
 * ── The one rule the whole file serves ──────────────────────────────────────
 *
 * **A number whose denominator cannot be stated is not returned.** Every result
 * below that could be an absence is a tagged union, not a number with a magic value:
 * `activeRatio` returns `no_observed_time` rather than `0`, and `foldWindow` returns
 * `covered: null` rather than a partial sum. Task 43 found D-22 live in shipped UI —
 * a withheld overtime figure rendered as "0 ph" because a type said `number |
 * undefined` where the server sends `null` — and on an aggregate screen the same
 * mistake reads as a fact about a hundred people rather than about one.
 *
 * ── What is deliberately not here ───────────────────────────────────────────
 *
 * No staleness clock. `web/shapes/overview.md` §4.6: nothing in this build compares
 * `Date.now()` to a data timestamp to decide that data is late. `degraded`,
 * `cache_status` and `coverage.complete` are the server's own answers and they are
 * carried through, not re-derived.
 */

import { addDays } from '../i18n/datetime';
import type { AggregateCoverage, DailyAggregateRow } from '../api/types';

/* ─────────────────────────────────────────────────────── the filter window ── */

export type OverviewWindow = 'today' | '7d' | '30d';

export const OVERVIEW_WINDOWS: readonly OverviewWindow[] = ['today', '7d', '30d'];

const WINDOW_DAYS: Readonly<Record<OverviewWindow, number>> = { today: 1, '7d': 7, '30d': 30 };

/**
 * The window from the URL. Anything unrecognised is `7d`.
 *
 * A default rather than an error because the query string is user-editable and a
 * dashboard that refuses to render because somebody mistyped a parameter is worse
 * than one that shows the default week — and the choice is visible in the control,
 * so nobody is misled about which window they are looking at.
 */
export function parseWindow(raw: string | null): OverviewWindow {
  return raw !== null && (OVERVIEW_WINDOWS as readonly string[]).includes(raw)
    ? (raw as OverviewWindow)
    : '7d';
}

/**
 * The dates a window covers, ending **today in the company's zone**.
 *
 * `today` is a parameter for the reason `AttendanceScreen` takes one: "today" is a
 * different day in Hanoi and in Berlin, and every clamp on this screen depends on
 * resolving it in `SessionUser.timezone` rather than on where the reader is sitting.
 */
export function windowRange(
  window: OverviewWindow,
  today: string,
): { readonly from: string; readonly to: string; readonly days: number } {
  const days = WINDOW_DAYS[window];
  return { from: addDays(today, -(days - 1)), to: today, days };
}

/* ───────────────────────────────────────────────────────────── the folding ── */

/** The four sums and the three completeness counts that qualify them. */
export interface DayTotals {
  readonly onlineSeconds: number;
  readonly activeSeconds: number;
  readonly idleSeconds: number;
  /** How many `(employee, date)` rows went into the sums above. */
  readonly rows: number;
  readonly complete: number;
  readonly partial: number;
  /** Computed, nothing observed. **Not** "did not work" — `openapi.yaml` says so. */
  readonly missing: number;
}

export interface TrendDay extends DayTotals {
  readonly date: string;
  readonly employees: number;
  /** `(employee, this date)` pairs the rollup has not reached, from `coverage`. */
  readonly notComputed: number;
}

export interface EmployeeTotals extends DayTotals {
  readonly employeeId: string;
}

export interface WindowFold {
  readonly requested: { readonly from: string; readonly to: string };
  /**
   * The dates the sums below actually cover — `null` when the page did not fully
   * cover a single date, in which case **no total is available at all** and the
   * caller renders a sentence rather than a number.
   *
   * `GET /activities/timeline` orders `session_date, employee_id` and caps `limit`
   * at 500, so on a truncated page every date strictly before the last one present
   * is fully represented and the last one is not. Narrowing the window is exact;
   * a partial sum labelled as a total is not. `web/shapes/overview.md` §8.
   */
  readonly covered: { readonly from: string; readonly to: string } | null;
  /** The page had more rows than it returned, so `covered` is narrower than `requested`. */
  readonly truncated: boolean;
  readonly totals: DayTotals;
  /** Distinct employees with at least one row inside `covered`. */
  readonly employeeCount: number;
  readonly perDay: readonly TrendDay[];
  readonly perEmployee: ReadonlyMap<string, EmployeeTotals>;
  /** `(employee, date)` pairs inside `covered` that the rollup has not reached. */
  readonly notComputedPairs: number;
}

const ZERO: DayTotals = {
  onlineSeconds: 0,
  activeSeconds: 0,
  idleSeconds: 0,
  rows: 0,
  complete: 0,
  partial: 0,
  missing: 0,
};

function add(totals: DayTotals, row: DailyAggregateRow): DayTotals {
  return {
    onlineSeconds: totals.onlineSeconds + row.online_seconds,
    activeSeconds: totals.activeSeconds + row.active_seconds,
    idleSeconds: totals.idleSeconds + row.idle_seconds,
    rows: totals.rows + 1,
    complete: totals.complete + (row.completeness_status === 'complete' ? 1 : 0),
    partial: totals.partial + (row.completeness_status === 'partial' ? 1 : 0),
    missing: totals.missing + (row.completeness_status === 'missing' ? 1 : 0),
  };
}

/** `${employee_id}|${session_date}` — the key `coverage.not_computed_days` names. */
export function pairKey(employeeId: string, date: string): string {
  return `${employeeId}|${date}`;
}

export function notComputedKeys(
  coverage: AggregateCoverage | null | undefined,
): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const day of coverage?.not_computed_days ?? []) {
    keys.add(pairKey(day.employee_id, day.session_date));
  }
  return keys;
}

/**
 * Fold one page of day rows into the totals, the trend and the per-employee slice.
 *
 * `hasMore` is `page.has_more` from the envelope, and it is the only input that
 * decides whether the window narrows. Everything else is arithmetic.
 */
export function foldWindow(
  rows: readonly DailyAggregateRow[],
  hasMore: boolean,
  requested: { readonly from: string; readonly to: string },
  coverage?: AggregateCoverage | null,
): WindowFold {
  const lastDate = rows.at(-1)?.session_date;
  // A truncated page's final date is the only one that can be missing rows, so the
  // covered window ends the day before it. When that lands before the start of the
  // requested window, not one date is fully covered and there is no total to give.
  const coveredTo = !hasMore || lastDate === undefined ? requested.to : addDays(lastDate, -1);
  const covered = coveredTo >= requested.from ? { from: requested.from, to: coveredTo } : null;

  const inWindow = covered === null ? [] : rows.filter((row) => row.session_date <= covered.to);
  const notComputed = notComputedKeys(coverage);

  let totals = ZERO;
  const employees = new Set<string>();
  const perDayTotals = new Map<string, DayTotals>();
  const perDayEmployees = new Map<string, Set<string>>();
  const perEmployee = new Map<string, EmployeeTotals>();

  for (const row of inWindow) {
    totals = add(totals, row);
    employees.add(row.employee_id);
    perDayTotals.set(row.session_date, add(perDayTotals.get(row.session_date) ?? ZERO, row));
    const seen = perDayEmployees.get(row.session_date) ?? new Set<string>();
    seen.add(row.employee_id);
    perDayEmployees.set(row.session_date, seen);
    const existing = perEmployee.get(row.employee_id) ?? { ...ZERO, employeeId: row.employee_id };
    perEmployee.set(row.employee_id, { ...add(existing, row), employeeId: row.employee_id });
  }

  const perDay: TrendDay[] = [];
  let notComputedPairs = 0;
  if (covered !== null) {
    const notComputedPerDay = new Map<string, number>();
    for (const key of notComputed) {
      const date = key.slice(key.indexOf('|') + 1);
      if (date < covered.from || date > covered.to) continue;
      notComputedPairs += 1;
      notComputedPerDay.set(date, (notComputedPerDay.get(date) ?? 0) + 1);
    }
    for (let date = covered.from; date <= covered.to; date = addDays(date, 1)) {
      perDay.push({
        ...(perDayTotals.get(date) ?? ZERO),
        date,
        employees: perDayEmployees.get(date)?.size ?? 0,
        notComputed: notComputedPerDay.get(date) ?? 0,
      });
    }
  }

  return {
    requested,
    covered,
    truncated: hasMore && covered !== null && covered.to !== requested.to,
    totals,
    employeeCount: employees.size,
    perDay,
    perEmployee,
    notComputedPairs,
  };
}

/* ─────────────────────────────────────────────────────────────── the ratio ── */

/**
 * `Σ active / Σ online`.
 *
 * **This is not Master §14.1's "productive ratio"**, and the label on screen says so
 * — `wp_daily_aggregates.productive_seconds` exists and no endpoint publishes it
 * (`web/shapes/overview.md` §4.5), so the classification-based ratio has no source
 * in this build. What this measures is how much of the *observed* online time was
 * active, which is a real and checkable number under its own name.
 *
 * A window that is half `partial` does not bias this the way it biases a *total*:
 * both terms come from the same rows, so an unobserved hour is missing from the
 * numerator and the denominator alike. What a partial window does cost is the
 * sample, so the caller renders the completeness counts beside it — never the
 * percentage alone.
 */
export type ActiveRatio =
  | {
      readonly kind: 'measured';
      /** 0..1. */
      readonly value: number;
      readonly activeSeconds: number;
      readonly onlineSeconds: number;
    }
  | { readonly kind: 'no_observed_time' };

export function activeRatio(totals: DayTotals): ActiveRatio {
  if (totals.onlineSeconds <= 0) return { kind: 'no_observed_time' };
  return {
    kind: 'measured',
    value: totals.activeSeconds / totals.onlineSeconds,
    activeSeconds: totals.activeSeconds,
    onlineSeconds: totals.onlineSeconds,
  };
}

/* ──────────────────────────────────────────────────────────── the presence ── */

/**
 * What today's aggregates say about one person, in five values that are never
 * collapsed into "online" and "offline".
 *
 * The standing rule of this build is that "an employee who did not work must never
 * be indistinguishable from an employee whose device was offline", and the two live
 * in different rows of this union: `observed_none` is *computed, nothing observed*,
 * `not_computed` is *the rollup has not reached them*, and `no_activity_data` is
 * *nothing arrived at all* — which is where an all-day-offline device lands and
 * which is why that value is **never** labelled "offline" on screen. The word
 * offline is a claim about a machine, and the only publisher of it is
 * `GET /devices`, behind `workforce.manage_devices`, which a manager does not hold.
 */
export type PresenceReading =
  'active' | 'idle' | 'observed_none' | 'not_computed' | 'no_activity_data';

export const PRESENCE_READINGS: readonly PresenceReading[] = [
  'active',
  'idle',
  'observed_none',
  'not_computed',
  'no_activity_data',
];

export interface PresenceCensus {
  readonly byEmployee: ReadonlyMap<string, PresenceReading>;
  readonly counts: Readonly<Record<PresenceReading, number>>;
  /** The roster size the census was taken over. */
  readonly headcount: number;
  /** Rows behind `active`/`idle`/`observed_none` whose day was only partly observed. */
  readonly partialRows: number;
}

export function presenceCensus(input: {
  readonly employeeIds: readonly string[];
  readonly todayRows: readonly DailyAggregateRow[];
  readonly notComputed: ReadonlySet<string>;
  readonly today: string;
}): PresenceCensus {
  const rows = new Map<string, DailyAggregateRow>();
  for (const row of input.todayRows) {
    if (row.session_date === input.today) rows.set(row.employee_id, row);
  }

  const byEmployee = new Map<string, PresenceReading>();
  const counts: Record<PresenceReading, number> = {
    active: 0,
    idle: 0,
    observed_none: 0,
    not_computed: 0,
    no_activity_data: 0,
  };
  let partialRows = 0;

  for (const employeeId of input.employeeIds) {
    const row = rows.get(employeeId);
    let reading: PresenceReading;
    if (row === undefined) {
      reading = input.notComputed.has(pairKey(employeeId, input.today))
        ? 'not_computed'
        : 'no_activity_data';
    } else {
      if (row.completeness_status === 'partial') partialRows += 1;
      reading =
        row.active_seconds > 0 ? 'active' : row.online_seconds > 0 ? 'idle' : 'observed_none';
    }
    byEmployee.set(employeeId, reading);
    counts[reading] += 1;
  }

  return { byEmployee, counts, headcount: input.employeeIds.length, partialRows };
}

/* ───────────────────────────────────────────────────────── the device strip ── */

export interface LivenessCensus {
  readonly online: number;
  /** Kept apart from `offline` on purpose — Task 19 keeps them apart. */
  readonly stale: number;
  readonly offline: number;
  /** `last_heartbeat_at === null`: never heard from, which is not the same as offline. */
  readonly neverReported: number;
  readonly total: number;
  /** `page.total` from the envelope, so a truncated page can say it is truncated. */
  readonly known: number;
}

export function livenessCensus(
  devices: readonly {
    readonly liveness: 'online' | 'stale' | 'offline';
    readonly last_heartbeat_at?: string | null;
  }[],
  total: number,
): LivenessCensus {
  let online = 0;
  let stale = 0;
  let offline = 0;
  let neverReported = 0;
  for (const device of devices) {
    if (device.last_heartbeat_at === null || device.last_heartbeat_at === undefined) {
      neverReported += 1;
      continue;
    }
    if (device.liveness === 'online') online += 1;
    else if (device.liveness === 'stale') stale += 1;
    else offline += 1;
  }
  return { online, stale, offline, neverReported, total, known: devices.length };
}
